/**
 * Alignment of the empty-state stack. `center` is the default — used for
 * full-bleed empty surfaces (an empty list, an empty run history). `start`
 * left-aligns everything so the empty-state slots into a sidebar or a card
 * column without looking adrift.
 */
export const EMPTY_STATE_ALIGN_CENTER = 'center';
export const EMPTY_STATE_ALIGN_START = 'start';

export type EmptyStateAlign =
  | typeof EMPTY_STATE_ALIGN_CENTER
  | typeof EMPTY_STATE_ALIGN_START;

export const EMPTY_STATE_SIZE_XS = 'xs';
export const EMPTY_STATE_SIZE_SM = 'sm';
export const EMPTY_STATE_SIZE_MD = 'md';
export const EMPTY_STATE_SIZE_LG = 'lg';

export type EmptyStateSize =
  | typeof EMPTY_STATE_SIZE_XS
  | typeof EMPTY_STATE_SIZE_SM
  | typeof EMPTY_STATE_SIZE_MD
  | typeof EMPTY_STATE_SIZE_LG;

/**
 * Per-size icon diameter, title font size, description font size, and stack
 * gap. `md` (default) is sized for a typical full-page empty surface; `sm`
 * fits inside a sidebar / popover; `lg` is for hero empty states. `xs` is
 * the densest variant — an inline single-line hint inside a node card or a
 * dense list row. At `xs` the title style drops from bold/onSurface to
 * regular/onSurfaceVariant so the text reads as a muted hint rather than a
 * heading (see {@link Title} in EmptyState.styled.ts).
 */
export const EMPTY_STATE_SIZE_TOKENS: Record<
  EmptyStateSize,
  {
    iconSize: string;
    titleFontSize: string;
    descriptionFontSize: string;
    gap: string;
    padY: string;
  }
> = {
  [EMPTY_STATE_SIZE_XS]: {
    iconSize: '16px',
    titleFontSize: '12px',
    descriptionFontSize: '11px',
    gap: '4px',
    padY: '4px',
  },
  [EMPTY_STATE_SIZE_SM]: {
    iconSize: '24px',
    titleFontSize: '13px',
    descriptionFontSize: '12px',
    gap: '6px',
    padY: '24px',
  },
  [EMPTY_STATE_SIZE_MD]: {
    iconSize: '36px',
    titleFontSize: '15px',
    descriptionFontSize: '13px',
    gap: '8px',
    padY: '48px',
  },
  [EMPTY_STATE_SIZE_LG]: {
    iconSize: '48px',
    titleFontSize: '18px',
    descriptionFontSize: '14px',
    gap: '10px',
    padY: '72px',
  },
};
